import { useMemo } from "react"
import { ColumnDef } from "@tanstack/react-table"
import { Text, Box } from "@chakra-ui/react"
import * as gqlTypes from "@hydrologiq/gql-experience/graphql"
import { usePaginatedSystems, SystemFilters } from "../hooks/usePaginatedSystems"
import { PaginatedTable, ServerPagination, ServerFiltering } from "../components/PaginatedTable"
import Link from "@hydrologiq/shared/components/Link"
import AssetsPopupCell from "./AssetsPopupCell"
import SystemRunDryCell from "./SystemRunDryCell"

interface SystemsTableProps {
  /** Pre-filter by customer ID */
  customerId?: string
  /** Pre-filter by site ID */
  siteId?: string
  /** Header actions (e.g. create button) */
  headerActions?: React.ReactNode
  /** Custom path prefix for system links. System ID will be appended. Defaults to "/systems/" */
  systemLinkPrefix?: string
  /** Custom empty state text */
  emptyStateText?: string
}

function SystemsTable({ customerId, siteId, headerActions, systemLinkPrefix, emptyStateText }: SystemsTableProps) {
  const {
    data: systems,
    pagination,
    filterOptions,
    loading,
    error,
    filters,
    sort,
    fetchPage,
    setFilters,
    setSort,
    clearFilters,
    refetch,
  } = usePaginatedSystems(customerId, siteId)

  // When customerId/siteId are provided, hide those columns (redundant in nested context)
  const showCustomerColumn = !customerId
  const showSiteColumn = !siteId

  const linkPrefix = systemLinkPrefix || "/systems/"

  const columns = useMemo<ColumnDef<gqlTypes.SystemListItem, any>[]>(() => {
    const cols: ColumnDef<gqlTypes.SystemListItem, any>[] = [
      {
        accessorKey: "name",
        header: "System Name",
        cell: ({ row }) => (
          <Link variant="primary" href={`${linkPrefix}${row.original.id}`} truncate tooltip>
            {row.original.name}
          </Link>
        ),
        meta: { filterVariant: "text" },
        size: 240,
        minSize: 180,
      },
    ]

    if (showCustomerColumn) {
      cols.push({
        accessorKey: "customer",
        header: "Customer",
        cell: ({ row }) => {
          const customerName = row.original.customerName
          const cId = row.original.customerId
          if (!customerName || !cId) return <Text color="fg.subtle">n/a</Text>
          return (
            <Link variant="primary" href={`/customers/${cId}`}>
              {customerName}
            </Link>
          )
        },
        meta: {
          filterVariant: "select",
          filterOptions: filterOptions?.customers?.map((c: { name: string; value: string }) => ({
            label: c.name,
            value: c.value,
          })),
        },
      })
    }

    if (showSiteColumn) {
      cols.push({
        accessorKey: "site",
        header: "Site",
        cell: ({ row }) => {
          const siteName = row.original.siteName
          const cId = row.original.customerId
          const sId = row.original.siteId
          if (!siteName || !cId || !sId) return <Text color="fg.subtle">n/a</Text>
          return (
            <Link variant="primary" href={`/customers/${cId}/sites/${sId}`}>
              {siteName}
            </Link>
          )
        },
        meta: {
          filterVariant: "select",
          filterOptions: filterOptions?.sites?.map((s: { name: string; value: string }) => ({
            label: s.name,
            value: s.value,
          })),
        },
      })
    }

    cols.push(
      {
        header: "Assets",
        cell: ({ row }) => (
          <AssetsPopupCell
            assetCount={row.original.assets?.length ?? 0}
            assets={row.original.assets ?? []}
          />
        ),
        enableColumnFilter: false,
        minSize: 150,
      },
      {
        accessorKey: "earliestRunDryMinutes",
        header: "Refuel needed in",
        cell: ({ row }) => (
          <SystemRunDryCell runDryMinutes={row.original.earliestRunDryMinutes} />
        ),
        enableColumnFilter: false,
        minSize: 120,
      },
      {
        accessorKey: "energy",
        header: "Energy Yesterday (kWh)",
        cell: ({ getValue }) => {
          const value = getValue() as number | null
          return <Text>{value ? Number(value).toFixed(2) : "n/a"}</Text>
        },
        enableColumnFilter: false,
        minSize: 150,
      },
      {
        accessorKey: "co2e",
        header: "CO2e Yesterday (kg)",
        cell: ({ getValue }) => {
          const value = getValue() as number | null
          return <Text>{value ? Number(value).toFixed(2) : "n/a"}</Text>
        },
        enableColumnFilter: false,
        minSize: 150,
      },
    )

    return cols
  }, [filterOptions, showCustomerColumn, showSiteColumn, linkPrefix])

  const serverPagination: ServerPagination | undefined = pagination ? {
    pagination: {
      page: pagination.page || 1,
      per_page: pagination.per_page || 10,
      total_count: pagination.total_count || 0,
      total_pages: pagination.total_pages || 0,
      has_next: pagination.has_next || false,
      has_prev: pagination.has_prev || false,
    },
    onPageChange: (page: number) => fetchPage(page),
    onPageSizeChange: (pageSize: number) => fetchPage(undefined, pageSize),
  } : undefined

  const serverFiltering: ServerFiltering<SystemFilters> = {
    filters: filters || {},
    sort,
    onFiltersChange: setFilters,
    onSortChange: setSort,
    onClearAll: clearFilters,
  }

  return (
    <PaginatedTable<gqlTypes.SystemListItem, SystemFilters>
      data={systems}
      columns={columns}
      loading={loading}
      error={error}
      onRetry={refetch}
      serverPagination={serverPagination}
      serverFiltering={serverFiltering}
      headerActions={headerActions}
      emptyState={{
        heading: "No systems",
        text: emptyStateText || "Please create a system to get started",
      }}
      variant="hydrologiq"
      entityName="systems"
    />
  )
}

export default SystemsTable
